import Axios from 'axios';
import BossApiParameters from "@/utils/Model/ApiService/BossApiParameters";
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import i18n from "@/plugins/translations";
import Vue from 'vue';
import VueCookies from "vue-cookies";
import FormDataService from "@/utils/Services/FormDataService";
import PushNotification from "@/models/User/PushNotification";

export default class AbstractApiService {

    constructor() {
        this.parameterService = new BossApiParameters();
        this._paginator = null;
        this._staticModel = null;
        this._disabledErrorPopup = false;
        this._beforeInsertMethod = null;

        Axios.defaults.baseURL = '/api';
        Axios.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded';
        Axios.defaults.headers.get['Content-Type'] = 'application/json';

        Axios.interceptors.response.use(function (response) {
            return response;
        }, function (error) {
            if(error && error.response && error.response.status && error.response.status === 401){ // unauthenticated
                if (!window.location.href.replace(window.location.origin,'').includes('/login')) {
                    VueCookies.set('intended_url', window.location.href, '1H');
                    window.location.href = window.config.BACKOFFICE_URL + '/login';
                    return Promise.reject(error);
                }
            }

            return Promise.reject(error);
        });
    }

    static convertToFormData(payload) {
        return FormDataService.create(payload);
    }

    /**
     * Run this method before insert in Vuex store
     * @param method
     * @returns {AbstractApiService}
     */
    beforeInsert(method = null) {
        if (typeof method !== 'function') {
            throw 'The value of beforeInsert should be a function';
        }

        this._beforeInsertMethod = method;

        return this;
    }

    /**
     * Disable the error info popup
     * @returns {AbstractApiService}
     */
    disableErrorInPopup() {
        this._disabledErrorPopup = true;

        return this;
    }

    /**
     * Add a where condition to the api call
     * @param column
     * @param value
     * @returns {AbstractApiService}
     */
    where(column, value) {
        this.parameterService.addWhere(column, value);

        return this;
    }

    filtered(filters = {}) {
        this.parameterService.filtered(filters);

        return this;
    }

    appended(appends = []) {
        this.parameterService.addAppends(appends);

        return this;
    }

    ordered(orders = {}) {
        this.parameterService.addOrders(orders);

        return this;
    }

    orderBy(column, direction = 'asc') {
        this.parameterService.addOrder(column, direction);

        return this;
    }

    with(withs = []) {
        this.parameterService.addWiths(withs);

        return this;
    }

    paginator(paginator = {}) {
        this._paginator = paginator;
        this.parameterService.setPaginate(true);

        if (paginator.currentPage !== undefined) {
            this.parameterService.setPage(paginator.currentPage);
        }

        if (paginator.perPage !== undefined) {
            this.parameterService.setLimit(paginator.perPage);
        }

        return this;
    }

    limit(limit) {
        this.parameterService.setLimit(limit);

        return this;
    }

    async returnMultiplePaginated(response, useStore = false) {
        if (response.data === undefined || response.data.data === undefined || !Array.isArray(response.data.data)) {
            if (Object.keys(response.data).length === 0) {
                this._paginator.currentPage = 1;
                this._paginator.lastPage = 1;
                this._paginator.total = 0;
                return [];
            }
            throw Error('paginated response is not an array');
        }

        this._paginator.currentPage = response.data.meta.current_page;
        this._paginator.lastPage = response.data.meta.last_page;
        this._paginator.perPage = parseInt(response.data.meta.per_page);
        this._paginator.total = response.data.meta.total;

        if (this._beforeInsertMethod) {
            this._beforeInsertMethod(response.data);
        }

        if (useStore) {
            await this._staticModel.insert(response.data.data);
            return this._staticModel.query().findIn(map(response.data.data, 'id'));
        }

        return response.data.data;
    }

    /**
     * resolve the api response of multiple item
     * @param response
     * @param useStore
     * @returns {PromiseLike<T>|Promise<T>|*|*[]}
     */
    async returnMultiple(response, useStore = false) {
        if (response.data === undefined || !(response.data instanceof Object)) {
            throw Error('response is not an array or object');
        }

        if (this._beforeInsertMethod) {
            this._beforeInsertMethod(response.data);
        }

        if (!Array.isArray(response.data)) {
            if (Object.keys(response.data).length > 0) {
                throw Error(this._staticModel.name + ' response is a single item instead of an array, use getSingle()');
            }

            return [];
        }

        if (useStore) {
            await this._staticModel.insert({data: response.data});
            return this._staticModel.query().with(this.parameterService.withs).findIn(map(response.data, 'id'));
        }

        return response.data;
    }

    /**
     * resolve the response of a single item
     * @param response
     * @param useStore
     * @returns {PromiseLike<T>|Promise<T>|*}
     */
    async returnSingle(response, useStore = true) {
        if (Array.isArray(response.data) && response.data.length === 0) {
            return null;
        }

        if (response.data === undefined || !(response.data instanceof Object) || Array.isArray(response.data)) {
            throw Error('response is not an object');
        }

        if (this._beforeInsertMethod) {
            this._beforeInsertMethod(response.data);
        }

        if (useStore) {
            await this._staticModel.insert({data: response.data});

            return this._staticModel.query().with(this.parameterService.withs).find(response.data.id);
        }

        return response.data;
    }

    _catch(error) {
        console.error(error);

        let message = error?.message;
        if (error?.response?.data?.message) {
            if (i18n.te(error.response.data.message)) {
                message = i18n.t(error.response.data.message);
            } else {
                message = error.response.data.message;
            }
            if (error.response.data.errors !== undefined && !isEmpty(error.response.data.errors)) {
                message += "<br />";
                Object.keys(error.response.data.errors).forEach(key => {
                    if (i18n.te(error.response.data.errors[key])) {
                        message += i18n.t(error.response.data.errors[key]) + "<br />";
                    } else {
                        message += error.response.data.errors[key] + "<br />";
                    }
                })
            }
        }

        if (!this._disabledErrorPopup) {
            if (!Vue.prototype.$dialog) {
                alert(message);
            } else {
                PushNotification.error(message);
            }
        }

        throw error;
    }
}
